import { AddProductDrawer } from "@/components/products/add-product-drawer";
import { AllProductsTable } from "@/components/tables/all-products";
import { ProductsTable } from "@/components/tables/products";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ApiError } from "@/lib/api/api-error";
import { buildMetadata } from "@/lib/metadata";
import { useAuth } from "@/providers/auth";
import {
  ProductService,
  type PaginatedProducts,
  type ProductSyncRequest,
} from "@/services/product-service";
import type {
  UnitOfMeasure,
  Warehouse,
} from "@/services/types/products";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { Loader2, Plus, RefreshCw, Search, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const SYNC_POLL_INTERVAL_MS = 5_000; // 5 seconds

const ALL_PRODUCTS_PAGE_SIZE = 10;

export const Route = createFileRoute("/_main/products/")({
  component: RouteComponent,
  loader: async () => {
    const [warehouses, productsPage, uoms] = await Promise.all([
      ProductService.listWarehouses(),
      ProductService.list({ page: 1, pageSize: ALL_PRODUCTS_PAGE_SIZE }),
      ProductService.listUoms(),
    ]);
    return {
      warehouses,
      productsPage: productsPage ?? {
        count: 0,
        next: null,
        previous: null,
        results: [],
        missing_base_uom_count: 0,
      },
      uoms,
    };
  },
  head: () => ({
    meta: buildMetadata({
      title: "Products",
      description:
        "Manage all your SKUs, pricing, and inventory levels in one place",
      keywords: ["products", "inventory", "sku", "stock"],
    }),
  }),
});

/**
 * Check if a sync request is currently in progress.
 */
function isSyncInProgress(syncRequest: ProductSyncRequest | null): boolean {
  if (!syncRequest) {
    return false;
  }
  return (
    syncRequest.status === "pending" || syncRequest.status === "processing"
  );
}

function toNumber(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

function buildWarehouseProductsByFilter(
  warehouses: Warehouse[],
  warehouseFilter: string,
  textFilter: string,
): Warehouse[] {
  const normalizedText = textFilter.trim().toLowerCase();
  const scopedWarehouses =
    !warehouseFilter || warehouseFilter === "-"
      ? warehouses
      : warehouses.filter((warehouse) => String(warehouse.id) === warehouseFilter);

  return scopedWarehouses.map((warehouse) => ({
    ...warehouse,
    product_stocks: warehouse.product_stocks.filter((product) => {
      if (!normalizedText) {
        return true;
      }

      const name = product.name?.toLowerCase() ?? "";
      const sku = product.sku.toLowerCase();
      return name.includes(normalizedText) || sku.includes(normalizedText);
    }),
  }));
}

function calculateInventorySummary(warehouses: Warehouse[], totalProducts: number) {
  const inStockProductIds = new Set<number>();
  for (const warehouse of warehouses) {
    for (const product of warehouse.product_stocks) {
      if (toNumber(product.quantity_available) > 0) {
        inStockProductIds.add(product.product_id);
      }
    }
  }

  const inStockProducts = inStockProductIds.size;
  const lowStockProducts = 0;
  const outOfStockProducts = Math.max(
    totalProducts - inStockProducts - lowStockProducts,
    0,
  );
  const inStockPercentage =
    totalProducts > 0 ? (inStockProducts / totalProducts) * 100 : 0;

  return {
    totalProducts,
    inStockProducts,
    lowStockProducts,
    outOfStockProducts,
    inStockPercentage,
  };
}

function RouteComponent() {
  const { warehouses, productsPage, uoms } = Route.useLoaderData() as {
    warehouses: Warehouse[];
    productsPage: PaginatedProducts;
    uoms: UnitOfMeasure[];
  };

  const queryClient = useQueryClient();
  const router = useRouter();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { user } = useAuth();
  const isWarehouseUser = user?.supplierRole === "warehouse_user";
  const wasSyncingRef = useRef(false);
  const [isAddProductDrawerOpen, setIsAddProductDrawerOpen] = useState(false);

  // Track the current sync request ID (set when user triggers sync or when we detect one in progress)
  const [currentSyncId, setCurrentSyncId] = useState<number | "latest">(
    "latest",
  );

  // Poll for sync status - uses ID if available, otherwise checks latest
  const syncStatusQuery = useQuery({
    queryKey: ["product-sync-status", currentSyncId],
    queryFn: () => ProductService.getSyncStatus(currentSyncId),
    refetchInterval: (query) => {
      // Poll every 3 seconds while pending/processing
      const syncRequest = query.state.data;
      return isSyncInProgress(syncRequest ?? null)
        ? SYNC_POLL_INTERVAL_MS
        : false;
    },
  });

  const isSyncing = isSyncInProgress(syncStatusQuery.data ?? null);

  // Refresh products when sync completes
  useEffect(() => {
    // Detect transition from syncing (true) to not syncing (false)
    if (wasSyncingRef.current && !isSyncing) {
      const syncRequest = syncStatusQuery.data;
      if (syncRequest?.status === "completed") {
        const created = syncRequest.products_created ?? 0;
        const updated = syncRequest.products_updated ?? 0;
        toast.success(
          `Sync completed! Created: ${created}, Updated: ${updated}`,
        );
      } else if (syncRequest?.status === "failed") {
        toast.error(`Sync failed: ${syncRequest.log || "Unknown error"}`);
      }
      // Refresh the paginated products query after sync completes.
      queryClient.invalidateQueries({ queryKey: ["inventory-products"] });
      // Reset to polling latest for future syncs
      setCurrentSyncId("latest");
    }
    wasSyncingRef.current = isSyncing;
  }, [isSyncing, syncStatusQuery.data, queryClient]);

  const syncMutation = useMutation({
    mutationFn: () => ProductService.syncProducts(),
    onSuccess: (data) => {
      if (data.status === "started" && data.sync_request) {
        toast.success(data.message);
        // Track this specific sync request
        setCurrentSyncId(data.sync_request.id);
      } else if (data.status === "already_running" && data.sync_request) {
        toast.info(data.message);
        // Track the existing sync request
        setCurrentSyncId(data.sync_request.id);
      } else if (data.status === "error") {
        toast.error(data.message);
      }
    },
    onError: (error: unknown) => {
      let message = "Failed to start sync. Please try again.";
      if (error instanceof ApiError) {
        const body = error.body as { message?: string } | undefined;
        if (body?.message) {
          message = body.message;
        }
      }
      toast.error(message);
      console.error("Sync error:", error);
    },
  });

  const createProductMutation = useMutation({
    mutationFn: ProductService.create,
    onSuccess: async () => {
      toast.success("Product added successfully.");
      await queryClient.invalidateQueries({ queryKey: ["inventory-products"] });
      await router.invalidate();
    },
    onError: (error: unknown) => {
      let message = "Failed to add product. Please try again.";
      if (error instanceof ApiError) {
        const body = error.body;
        if (typeof body === "object" && body !== null) {
          const firstEntry = Object.values(body)[0];
          if (Array.isArray(firstEntry) && firstEntry[0]) {
            message = String(firstEntry[0]);
          } else if (typeof firstEntry === "string") {
            message = firstEntry;
          }
        }
      }
      toast.error(message);
    },
  });

  const configureUomMutation = useMutation({
    mutationFn: ({
      productId,
      payload,
    }: {
      productId: number;
      payload: {
        base_stock_uom: number | null;
        default_uom: number | null;
        packaging_units: { uom_id: number; factor: number }[];
      };
    }) => ProductService.configureUom(productId, payload),
    onSuccess: async () => {
      toast.success("Product UOM configuration updated.");
      await queryClient.invalidateQueries({ queryKey: ["inventory-products"] });
      await router.invalidate();
    },
    onError: (error: unknown) => {
      let message = "Failed to update UOM configuration. Please try again.";
      if (error instanceof ApiError) {
        const body = error.body;
        if (typeof body === "object" && body !== null) {
          const firstEntry = Object.values(body)[0];
          if (Array.isArray(firstEntry) && firstEntry[0]) {
            message = String(firstEntry[0]);
          } else if (typeof firstEntry === "string") {
            message = firstEntry;
          }
        }
      }
      toast.error(message);
    },
  });

  const isButtonDisabled = syncMutation.isPending || isSyncing;

  const [globalTextFilter, setGlobalTextFilter] = useState("");
  const [globalWarehouseFilter, setGlobalWarehouseFilter] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogStatusFilter, setCatalogStatusFilter] = useState<
    "all" | "in_stock" | "out_of_stock"
  >("all");
  const [catalogMissingUomOnly, setCatalogMissingUomOnly] = useState(false);
  const [catalogPageIndex, setCatalogPageIndex] = useState(0);
  const [filteredResults, setFilteredResults] = useState(() =>
    buildWarehouseProductsByFilter(warehouses, "", ""),
  );

  useEffect(() => {
    setFilteredResults(
      buildWarehouseProductsByFilter(
        warehouses,
        globalWarehouseFilter,
        globalTextFilter,
      ),
    );
  }, [warehouses, globalWarehouseFilter, globalTextFilter]);

  const productsQuery = useQuery({
    queryKey: [
      "inventory-products",
      catalogPageIndex,
      catalogSearch,
      catalogStatusFilter,
      catalogMissingUomOnly,
    ],
    queryFn: async () =>
      ProductService.list({
        page: catalogPageIndex + 1,
        pageSize: ALL_PRODUCTS_PAGE_SIZE,
        filters: {
          search: catalogSearch || undefined,
          status:
            catalogStatusFilter === "all" ? undefined : catalogStatusFilter,
          missing_uom: catalogMissingUomOnly ? "true" : undefined,
        },
      }),
    initialData:
      !catalogSearch &&
      catalogStatusFilter === "all" &&
      !catalogMissingUomOnly &&
      catalogPageIndex === 0
        ? productsPage
        : undefined,
  });

  useEffect(() => {
    setCatalogPageIndex(0);
  }, [catalogSearch, catalogStatusFilter, catalogMissingUomOnly]);

  const activeProductsPage = productsQuery.data ?? productsPage;
  const summary = calculateInventorySummary(warehouses, productsPage.count);
  const warehouseCount = warehouses.length;
  const stockWarehouses = warehouses.filter(
    (warehouse) => warehouse.is_active && warehouse.is_stock_location,
  );

  return (
    <>
      <AddProductDrawer
        open={isAddProductDrawerOpen}
        onOpenChange={setIsAddProductDrawerOpen}
        warehouses={stockWarehouses}
        uoms={uoms}
        isSubmitting={createProductMutation.isPending}
        onSubmit={async (payload) => {
          await createProductMutation.mutateAsync(payload);
        }}
      />

      <div className="space-y-6 px-4 pb-24 pt-4 md:p-8 md:pb-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className={isMobile ? "space-y-5" : undefined}>
          <h1 className="font-semibold text-2xl tracking-tight">Products</h1>
          <p className={`text-muted-foreground text-sm ${isMobile ? "text-left" : ""}`}>
            {isMobile
              ? "Manage your full catalogue and add new products"
              : "Manage all your SKUs, pricing, and inventory levels in one place"}
          </p>
        </div>
        <div className="hidden flex-wrap gap-3 md:flex">
          <Button variant="outline" size="lg" className="rounded-xl" asChild>
            <Link to="/products/upload">
              <Upload className="mr-2 h-4 w-4" />
              Upload Document
            </Link>
          </Button>
          {!isWarehouseUser && (
            <Button
              onClick={() => syncMutation.mutate()}
              disabled={isButtonDisabled}
              size="lg"
              variant="outline"
              className="cursor-pointer rounded-xl bg-background"
            >
              {isButtonDisabled ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {isSyncing ? "Syncing in Progress..." : "Sync Stock Levels"}
            </Button>
          )}
          <Button
            size="lg"
            className="rounded-xl bg-foreground text-background hover:bg-foreground/90"
            onClick={() => setIsAddProductDrawerOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
        <InventoryKpiCard
          title={isMobile ? "Total" : "Total Products"}
          value={formatInteger(summary.totalProducts)}
          description={
            isMobile
              ? "All SKUs"
              : `Across ${formatInteger(warehouseCount)} warehouses`
          }
          dotClassName="bg-foreground"
        />
        <InventoryKpiCard
          title="In Stock"
          value={formatInteger(summary.inStockProducts)}
          description={
            isMobile
              ? `${formatPercent(summary.inStockPercentage)}% of all`
              : `${formatPercent(summary.inStockPercentage)}% of catalog products`
          }
          dotClassName="bg-emerald-600"
        />
        <InventoryKpiCard
          title="Low Stock"
          value={formatInteger(summary.lowStockProducts)}
          description={isMobile ? "Reorder soon" : "Needs reorder soon"}
          dotClassName="bg-amber-600"
        />
        <InventoryKpiCard
          title="Out of Stock"
          value={formatInteger(summary.outOfStockProducts)}
          description={isMobile ? "Unavailable" : "Currently unavailable"}
          dotClassName="bg-rose-600"
        />
      </div>

      <Tabs defaultValue="by-warehouse" className="w-full">
        <TabsList className="inline-grid h-auto grid-cols-2 rounded-none border-b bg-transparent p-0">
          <TabsTrigger
            value="by-warehouse"
            className="!rounded-none border-b-2 border-b-transparent px-2 py-3 text-base text-muted-foreground data-[state=active]:border-b-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none md:px-6"
          >
            By Warehouse
          </TabsTrigger>
          <TabsTrigger
            value="all-products"
            className="!rounded-none border-b-2 border-b-transparent px-2 py-3 text-base text-muted-foreground data-[state=active]:border-b-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none md:px-6"
          >
            All Products
          </TabsTrigger>
        </TabsList>

        <TabsContent value="by-warehouse" className="space-y-6 pt-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="w-full md:w-[280px]">
              <Select
                value={globalWarehouseFilter || "-"}
                onValueChange={setGlobalWarehouseFilter}
              >
                <SelectTrigger className="h-10 w-full rounded-lg">
                  <SelectValue placeholder="Select Warehouse" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-">Select Warehouse</SelectItem>
                  {warehouses.map((warehouse) => (
                    <SelectItem value={String(warehouse.id)} key={warehouse.id}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="relative w-full md:w-[320px]">
              <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by SKU or name..."
                value={globalTextFilter}
                onChange={(event) =>
                  setGlobalTextFilter(event.target.value.toLowerCase())
                }
                className="w-full pl-9"
              />
            </div>
          </div>

          {filteredResults.map((warehouse) => (
            <ProductsTable
              data={warehouse.product_stocks}
              warehouse={warehouse}
              uoms={uoms}
              isConfigureUomSubmitting={configureUomMutation.isPending}
              onConfigureUomSubmit={async (product, payload) => {
                await configureUomMutation.mutateAsync({
                  productId: product.id,
                  payload,
                });
              }}
              key={warehouse.id}
            />
          ))}
        </TabsContent>

        <TabsContent value="all-products" className="space-y-6 pt-4">
          <AllProductsTable
            data={activeProductsPage.results}
            totalCount={activeProductsPage.count}
            missingBaseUomCount={activeProductsPage.missing_base_uom_count ?? 0}
            currentPage={catalogPageIndex}
            pageCount={Math.max(
              Math.ceil(activeProductsPage.count / ALL_PRODUCTS_PAGE_SIZE),
              1,
            )}
            onPageChange={setCatalogPageIndex}
            searchValue={catalogSearch}
            onSearchChange={setCatalogSearch}
            statusFilter={catalogStatusFilter}
            onStatusFilterChange={setCatalogStatusFilter}
            missingUomOnly={catalogMissingUomOnly}
            onMissingUomOnlyChange={setCatalogMissingUomOnly}
            isLoading={productsQuery.isFetching}
            uoms={uoms}
            isConfigureUomSubmitting={configureUomMutation.isPending}
            onConfigureUomSubmit={async (product, payload) => {
              await configureUomMutation.mutateAsync({
                productId: product.id,
                payload,
              });
            }}
          />
        </TabsContent>
      </Tabs>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 p-4 backdrop-blur md:hidden">
        <Button
          size="lg"
          className="h-12 w-full rounded-xl bg-foreground text-background text-lg hover:bg-foreground/90"
          onClick={() => setIsAddProductDrawerOpen(true)}
        >
          <Plus className="mr-2 h-5 w-5" />
          Add product
        </Button>
      </div>
      </div>
    </>
  );
}

function InventoryKpiCard({
  title,
  value,
  description,
  dotClassName,
}: {
  title: string;
  value: string;
  description: string;
  dotClassName: string;
}) {
  return (
    <Card className="gap-2 rounded-2xl border-border/70 py-4 shadow-none">
      <CardHeader className="flex flex-row items-start justify-between gap-4 px-4 pb-0">
        <CardTitle className="font-semibold text-base text-foreground md:text-base">
          {title}
        </CardTitle>
        <span className={`h-3 w-3 rounded-full ${dotClassName}`} />
      </CardHeader>
      <CardContent className="space-y-1 px-4 pt-0">
        <div className="font-semibold text-2xl tracking-tight md:text-3xl">
          {value}
        </div>
        <p className="text-muted-foreground text-xs md:text-sm">{description}</p>
      </CardContent>
    </Card>
  );
}
